import React, { useMemo, useCallback, useEffect } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import { getUserDataAction, getUsersAction } from 'store/asyncActions';
import { selectDeleteUserState } from 'store/selectors/users';
import Button, { ButtonKind, ButtonType } from 'components/common/Button';
import Form from 'components/Forms/Form';
import { BigWarningIcon, DefaultUserIcon, LoadingAnimatedIcon } from 'icons';
import { selectAuthData } from 'store/selectors/auth';
import Title, { TitleKind } from 'components/common/Title';
import { deleteUserAction } from 'store/asyncActions/users/deleteUserReducer';
import { clearDeleteUserState } from 'store/slices/usersSlice';

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 70vw;
  gap: 2rem;
  margin-top: 2rem;

  ${({ theme }) =>
    css`
      font-family: ${theme.fonts.primary.family};
      background-color: ${theme.colors.white};

      @media screen and (min-width: ${theme.mediaQueries.tablet}) {
        width: 50vw;
      }

      @media screen and (min-width: ${theme.mediaQueries.largerTabet}) {
        width: 35vw;
      }
    `};
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.5rem;

  @media screen and (min-width: ${({ theme }) => theme.mediaQueries.mobile}) {
    flex-direction: row;
  }
`;

const DeleteUserForm = ({ toggle, userData }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { deleteUserLoading, deleteUserSuccess, deleteUserError } = useSelector(selectDeleteUserState, shallowEqual);
  const { userId } = useSelector(selectAuthData, shallowEqual);

  const { icon, firstName, lastName, id } = userData;

  const useFormProps = useMemo(
    () => ({
      mode: 'onSubmit',
    }),
    []
  );

  const onSubmit = useCallback(() => {
    dispatch(deleteUserAction(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (deleteUserSuccess) {
      dispatch(getUsersAction());
      dispatch(getUserDataAction(userId));
      toggle();
    }
  }, [dispatch, toggle, deleteUserSuccess, userId]);

  useEffect(
    () => () => {
      dispatch(clearDeleteUserState());
      toast.clearWaitingQueue();
    },
    [dispatch]
  );

  useEffect(() => {
    if (deleteUserError?.message) {
      toast.error(deleteUserError.message);
    }
  }, [deleteUserError?.message]);

  return (
    <StyledForm useFormProps={useFormProps} onSubmit={onSubmit}>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
        `}
      >
        <BigWarningIcon />
        <Title
          css={css`
            font-weight: 500;
            color: ${theme.colors.black};
            margin: 2rem 0;
          `}
          kind={TitleKind.h3}
        >
          Are you sure you want to delete this user?
        </Title>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 0.75rem;
            font-weight: 500;
          `}
        >
          {icon ? (
            <img
              src={icon}
              alt="UserIcon"
              css={css`
                width: 3.5rem;
                height: 3.5rem;
              `}
            />
          ) : (
            <DefaultUserIcon height="3.5rem" width="3.5rem" />
          )}
          {`${firstName} ${lastName}`}
        </div>
      </div>
      <ButtonsContainer>
        <Button
          css={css`
            width: 100%;
            text-align: center;
            border-radius: ${theme.border.radius.sm};
          `}
          disabled={deleteUserLoading}
          kind={ButtonKind.Secondary}
          onClick={toggle}
        >
          Cancel
        </Button>
        <Button
          css={css`
            width: 100%;
            text-align: center;
            border-radius: ${theme.border.radius.sm};
            color: ${theme.colors.black};
          `}
          disabled={deleteUserLoading}
          type={ButtonType.Submit}
        >
          {deleteUserLoading ? (
            <span
              css={css`
                display: flex;
                height: ${theme.fonts.primary.size.xl};
              `}
            >
              <LoadingAnimatedIcon />
            </span>
          ) : (
            'Delete User'
          )}
        </Button>
      </ButtonsContainer>
    </StyledForm>
  );
};

DeleteUserForm.propTypes = {
  toggle: PropTypes.func,
  userData: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    lastName: PropTypes.string,
    firstName: PropTypes.string,
    email: PropTypes.string,
    role: PropTypes.string,
    icon: PropTypes.string,
    Icon: PropTypes.oneOfType([PropTypes.node, PropTypes.element, PropTypes.elementType]),
  }).isRequired,
};

DeleteUserForm.defaultProps = {
  toggle: null,
};

export default DeleteUserForm;
